import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { WallboardViewService } from '../wallboard-view.service';
import { AgentCounterModuleDataObj, WVCustom, WVMain } from '../wallboard-view.model';
import { CounterSettingsTransferObj, ModuleType } from '../../wallboards.model';
import { Chart } from 'chart.js';

@Component({
    selector: 'cv-wallboard-view-custom-agent-counter',
    templateUrl: './wallboard-view-custom-agent-counter.component.html',
    styleUrls: ['./wallboard-view-custom-agent-counter.component.scss']
})
export class WallboardViewCustomAgentCounterComponent implements OnInit, OnDestroy {
    @Input() index;
    @Input() height;
    @Input() width;
    @Input() firstSlide;

    public wvf: WVMain;
    public wvcf: WVCustom;
    public agentCounterState: CounterSettingsTransferObj = { CounterTitle: "", ShowLabels: false, chartOptions: null };
    public agentCounterData: AgentCounterModuleDataObj;

    public chartLoaded: boolean = false;
    public slideError: boolean = false;
    public AgentStateTypeEnum = {
        Invalid: 0,
        Inbound: 1,
        Outbound: 2,
        Available: 3,
        Unavailable: 4,
        0: "Invalid",
        1: "Inbound",
        2: "Outbound",
        3: "Available",
        4: "Unavailable",
    };

    private updateTimerRef: NodeJS.Timeout = null;
    private isDestroyed: boolean = false;
    private _hasBackdrop: boolean = false;
    private _moduleKey: string = "";

    get hasBackdrop(): boolean {
        return this._hasBackdrop;
    }

    get moduleKey(): string {
        return this._moduleKey;
    }

    constructor(
        private service: WallboardViewService
    ) { }

    ngOnInit(): void {
        this.wvf = this.service.WVMain;
        this.wvcf = this.service.WVCustom;

        this._hasBackdrop = this.wvcf?.Slide.HasBackdrop ?? false;
        this._moduleKey = this.wvcf?.Slide.WallboardModuleDetails[this.index].WallboardModuleKey ?? "";

        this.chartLoaded = false;
        // Enum for friendlier indices/object property keys to this.agentCounterData.AgentStateCategories

        let moduleState = this.service.loadModuleState(ModuleType.AgentCounter, this.wvf.WallboardCreatorID, this.moduleKey).then(response => {
            if (this.isDestroyed)
                return;

            this.agentCounterState = response.counterObj;
        });

        let agentCounterModuleData = this.service.getAgentCounterModuleData(this.wvcf.Slide.WallboardID, this.moduleKey).then(response => {
            if (this.isDestroyed)
                return;

            this.agentCounterData = response;
        });

        Promise.all([moduleState, agentCounterModuleData]).then(responses => {
            if (!this.agentCounterData) {
                let elementContext = document.getElementsByClassName("chart" + this.moduleKey)[0] as HTMLCanvasElement;

                const dataChart = {
                    datasets: [{
                        data: [1, 1, 1, 1, 1],
                        borderColor: "transparent",
                        borderWidth: 5,
                        backgroundColor: [
                            'rgba(61,150,73,1)', // Available
                            'rgba(124,82,191,1)', // Inbound
                            'rgba(228,179,33,1)', // Outbound
                            'rgba(241,102,34,1)', // ACW
                            'rgba(189,32,38,1)' // Unavailable
                        ],
                    }]
                };

                if (this.agentCounterState.chartOptions != null) {
                    this.agentCounterState.chartOptions.destroy();
                    this.agentCounterState.chartOptions = null;
                }

                this.agentCounterState.chartOptions = new Chart(elementContext, {
                    type: 'doughnut',
                    data: dataChart,
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            tooltip: { enabled: false },
                            legend: { display: false },
                            datalabels: {
                                display: false,
                            }
                        },
                        layout: {
                            padding: {
                                left: 32,
                                right: 32,
                                top: 0,
                                bottom: 32
                            }
                        }
                    }
                });

                this.agentCounterState.chartOptions.options.animation = false;
            }

            // If not first slide, notify WallboardViewCustom this module is ready.
            if (!this.firstSlide) {
                let dup = false;

                for (let i = 0; i < this.wvcf.ReadyModules.length; i++) {
                    if (this.moduleKey == this.wvcf.ReadyModules[i]) {
                        dup = true;
                        break;
                    }
                }

                if (!dup)
                    this.wvcf.ReadyModules.push(this.moduleKey);
            }

            this.runSlide();
        });
    }

    ngOnDestroy(): void {
        // End data updates
        if (this.updateTimerRef != null)
            clearInterval(this.updateTimerRef);

        if (this.agentCounterState.chartOptions != null) {
            this.agentCounterState.chartOptions.destroy();
            this.agentCounterState.chartOptions = null;
        }

        this.isDestroyed = true;
    }

    runSlide() {
        if (this.wvf.RunSlide) {
            this.setInitialChart(); //this uses a different method so the first animation will be unique
            this.innerTransition();
        }
        else {
            setTimeout(() => {
                this.runSlide();
            }, 1000);
        }
    }

    async innerTransition() {
        if (this.updateTimerRef || this.isDestroyed)
            return;

        // Reload the data every 5 secs.
        this.updateTimerRef = setInterval(async () => {
            let response = await this.service.getAgentCounterModuleData(this.wvcf.Slide.WallboardID, this.moduleKey);
      
            if (this.isDestroyed)
                return;

            this.agentCounterData = response;

            let counterHiddenData: any = {}; //this will be used to allow minimun chart slices while still showing the correct label value.
            if ((this.agentCounterData.AvailableCount > 0) && this.agentCounterData.AvailableCount < this.agentCounterData.TotalCount * .1)
                counterHiddenData.AvailableCount = this.agentCounterData.TotalCount * .1;
            else
                counterHiddenData.AvailableCount = this.agentCounterData.AvailableCount;

            if ((this.agentCounterData.InboundCount > 0) && this.agentCounterData.InboundCount < this.agentCounterData.TotalCount * .1)
                counterHiddenData.InboundCount = this.agentCounterData.TotalCount * .1;
            else
                counterHiddenData.InboundCount = this.agentCounterData.InboundCount;

            if ((this.agentCounterData.OutboundCount > 0) && this.agentCounterData.OutboundCount < this.agentCounterData.TotalCount * .1)
                counterHiddenData.OutboundCount = this.agentCounterData.TotalCount * .1;
            else
                counterHiddenData.OutboundCount = this.agentCounterData.OutboundCount;

            if ((this.agentCounterData.AcwCount > 0) && this.agentCounterData.AcwCount < this.agentCounterData.TotalCount * .1)
                counterHiddenData.AcwCount = this.agentCounterData.TotalCount * .1;
            else
                counterHiddenData.AcwCount = this.agentCounterData.AcwCount;

            if ((this.agentCounterData.UnavailableCount > 0) && this.agentCounterData.UnavailableCount < this.agentCounterData.TotalCount * .1)
                counterHiddenData.UnavailableCount = this.agentCounterData.TotalCount * .1;
            else
                counterHiddenData.UnavailableCount = this.agentCounterData.UnavailableCount;

            // If all 4 are 0 then change hidden value so they all display equally
            if (counterHiddenData.AvailableCount == 0 && counterHiddenData.InboundCount == 0 && counterHiddenData.OutboundCount == 0 && counterHiddenData.UnavailableCount == 0 && counterHiddenData.AcwCount == 0) {
                counterHiddenData.AvailableCount = 1;
                counterHiddenData.InboundCount = 1;
                counterHiddenData.OutboundCount = 1;
                counterHiddenData.AcwCount = 1;
                counterHiddenData.UnavailableCount = 1;
            }

            let preparedData = [
                counterHiddenData.AvailableCount,
                counterHiddenData.InboundCount,
                counterHiddenData.OutboundCount,
                counterHiddenData.AcwCount,
                counterHiddenData.UnavailableCount
            ];

            this.agentCounterState.chartOptions.data.datasets[0].data = preparedData;

            if (this.agentCounterState.chartOptions !== null)
                this.agentCounterState.chartOptions.update();

            /* function (error) {
               // If data fails to load, display slide error message (dweeb) on module
               this.slideError = true;
            }*/
        }, WallboardViewService.REALTIME_DATA_REFRESH_INTERVAL);
    }

    setInitialChart() {
        if (this.agentCounterData == null)
            return;

        this.chartLoaded = true;

        setTimeout(() => {
            this.SizeTotalText();

            if (this.chartLoaded == true)
                this.SizeLegendText();
        }, 100);

        if (this.agentCounterState.chartOptions != null) {
            this.agentCounterState.chartOptions.destroy();
            this.agentCounterState.chartOptions = null;
        }

        let elementContext = document.getElementsByClassName("chart" + this.moduleKey)[0] as HTMLCanvasElement;

        let colors = [
            'rgba(61,150,73,1)', // Available
            'rgba(124,82,191,1)', // Inbound
            'rgba(228,179,33,1)', // Outbound
            "rgba(241,102,34,1)", // ACW
            'rgba(189,32,38,1)' // Unavailable
        ];

        let counterHiddenData: any = {
            AvailableCount: 0,
            InboundCount: 0,
            OutboundCount: 0,
            AcwCount: 0,
            UnavailableCount: 0,
            TotalCount: 0,
            AgentStateCategories: []
        }; // This will be used to allow minimun chart slices while still showing the correct label value.

        if ((this.agentCounterData.AvailableCount > 0) && this.agentCounterData.AvailableCount < this.agentCounterData.TotalCount * .1)
            counterHiddenData.AvailableCount = this.agentCounterData.TotalCount * .1;
        else
            counterHiddenData.AvailableCount = this.agentCounterData.AvailableCount;

        if ((this.agentCounterData.InboundCount > 0) && this.agentCounterData.InboundCount < this.agentCounterData.TotalCount * .1)
            counterHiddenData.InboundCount = this.agentCounterData.TotalCount * .1;
        else
            counterHiddenData.InboundCount = this.agentCounterData.InboundCount;

        if ((this.agentCounterData.OutboundCount > 0) && this.agentCounterData.OutboundCount < this.agentCounterData.TotalCount * .1)
            counterHiddenData.OutboundCount = this.agentCounterData.TotalCount * .1;
        else
            counterHiddenData.OutboundCount = this.agentCounterData.OutboundCount;

        if ((this.agentCounterData.AcwCount > 0) && this.agentCounterData.AcwCount < this.agentCounterData.TotalCount * .1)
            counterHiddenData.AcwCount = this.agentCounterData.TotalCount * .1;
        else
            counterHiddenData.AcwCount = this.agentCounterData.AcwCount;

        if ((this.agentCounterData.UnavailableCount > 0) && this.agentCounterData.UnavailableCount < this.agentCounterData.TotalCount * .1)
            counterHiddenData.UnavailableCount = this.agentCounterData.TotalCount * .1;
        else
            counterHiddenData.UnavailableCount = this.agentCounterData.UnavailableCount;

        // If all 4 are 0 then change hidden value so they all display equally
        if (counterHiddenData.AvailableCount == 0 && counterHiddenData.InboundCount == 0 && counterHiddenData.OutboundCount == 0 && counterHiddenData.UnavailableCount == 0 && counterHiddenData.AcwCount == 0) {
            counterHiddenData.AvailableCount = 1;
            counterHiddenData.InboundCount = 1;
            counterHiddenData.OutboundCount = 1;
            counterHiddenData.AcwCount = 1;
            counterHiddenData.UnavailableCount = 1;
        }

        let preparedData = [
            counterHiddenData.AvailableCount,
            counterHiddenData.InboundCount,
            counterHiddenData.OutboundCount,
            counterHiddenData.AcwCount,
            counterHiddenData.UnavailableCount
        ];

        let preparedLabels = [
            this.agentCounterData.AgentStateCategories[this.AgentStateTypeEnum.Available],
            this.agentCounterData.AgentStateCategories[this.AgentStateTypeEnum.Inbound],
            this.agentCounterData.AgentStateCategories[this.AgentStateTypeEnum.Outbound],
            'ACW',
            this.agentCounterData.AgentStateCategories[this.AgentStateTypeEnum.Unavailable],
        ];

        this.agentCounterState.chartOptions = new Chart(elementContext, {
            type: 'doughnut',
            data: {
                datasets: [{
                    data: preparedData,
                    backgroundColor: colors,
                    borderColor: "transparent",
                    borderWidth: 5,
                }],
                labels: [
                    preparedLabels
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    tooltip: { enabled: false },
                    legend: { display: false },
                    datalabels: {
                        anchor: function (k) {
                            return 'center';
                        },
                        align: function (k) {
                            return 'center';
                        },
                        offset: function (k) {
                            return 0;
                        },
                        display: function (context) {
                            return +context.dataset.data[context.dataIndex] > 0;
                        },
                        backgroundColor: function (context) {
                            return null;
                        },
                        borderRadius: function (k) {
                            return 0;
                        },
                        borderWidth: function (k) {
                            return 0;
                        },
                        padding: function (context) {
                            return 0;
                        },
                        color: '#fff',
                        font: function (context) {
                            //min h (min module + min screen) = 87
      
                            let width = context.chart.width - 64;
                            let height = context.chart.height - 64;
                            let size = width > height ? Math.round(height / 10) : Math.round(width / 12);
                            let minSize = 12;
      
                            return {
                                size: size < minSize ? minSize : size,
                                weight: 'bold'
                            }
                        },
                        formatter: (value, context) => {
                            // To enforce minimum slice size the charts data value may not be the actual agent data. If it is not use the real data as the label.
                            let labelVal = value;
                            if (context.dataIndex == 0 && context.dataset.data[context.dataIndex] != this.agentCounterData.AvailableCount)
                                labelVal = this.agentCounterData.AvailableCount;
                            else if (context.dataIndex == 1 && context.dataset.data[context.dataIndex] != this.agentCounterData.InboundCount)
                                labelVal = this.agentCounterData.InboundCount;
                            else if (context.dataIndex == 2 && context.dataset.data[context.dataIndex] != this.agentCounterData.OutboundCount)
                                labelVal = this.agentCounterData.OutboundCount;
                            else if (context.dataIndex == 3 && context.dataset.data[context.dataIndex] != this.agentCounterData.AcwCount)
                                labelVal = this.agentCounterData.AcwCount;
                            else if (context.dataIndex == 4 && context.dataset.data[context.dataIndex] != this.agentCounterData.UnavailableCount)
                                labelVal = this.agentCounterData.UnavailableCount;
      
                            return labelVal;
                        }
                    }
                },
                layout: {
                    padding: {
                        left: 32,
                        right: 32,
                        top: 0,
                        bottom: 32
                    }
                }
            }
        });

        this.agentCounterState.chartOptions.options.animation = false;

        this.SizeTitleText();
    }

    SizeTitleText() {
        setTimeout(() => {
            let divElem = $('.agentTitle' + this.moduleKey);
            let divMax = divElem.parent().height();

            divElem.css('font-size', divMax * 0.66);
            let divMaxWidth = divElem.parent().width();
            let divSize = divElem.height();
            let divWidth = divElem.width();

            divElem.css('visibility', 'hidden');

            while (divSize < divMax && divWidth <= divMaxWidth) {
                divElem.css('font-size', +divElem.css('font-size').slice(0, -2) - 0 + 5 + 'px');
                divSize = divElem.height();
                divWidth = divElem.width();

                if (divSize == 0) return;
            }

            while (divSize > divMax || divWidth > divMaxWidth) {
                divElem.css('font-size', +divElem.css('font-size').slice(0, -2) - 1 + 'px');
                divSize = divElem.height();
                divWidth = divElem.width();
            }

            divElem.css('visibility', 'visible');
        });
    }

    SizeTotalText() {
        let divElem = $('.actot' + this.moduleKey);
        let divMax = divElem.parent().height();
        let divMaxWidth = divElem.parent().width();

        let totalElem = $('.actot' + this.moduleKey);
        let adjustedHeight = divMax > divMaxWidth ? Math.floor((divMaxWidth / 1.18) * 0.67) : divMax * 0.67;
        totalElem.css("font-size", Math.floor(adjustedHeight * .19) + "px");
    }

    SizeLegendText() {
        let divElem = $('.acleg' + this.moduleKey);
        divElem.parent().parent().parent().removeAttr('hidden');

        if (divElem.parent().parent().parent().height() > 0) {
            let sizeElem = divElem.parent().parent().parent().length;
            let divMax = divElem.parent().parent().parent().get(sizeElem - 1).clientHeight / 2;
            let divMaxWidth = divElem.parent().parent().parent().get(sizeElem - 1).clientWidth / 2;

            let adjustedHeight = divMax > divMaxWidth ? Math.floor((divMaxWidth / 1.18) * 0.67) : divMax * 0.67;
            divElem.css("font-size", Math.floor(adjustedHeight) + "px");
        }
    }
}
